import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { StandardButton } from '../styles/StandardButtons';
import { AuthBanner } from './AuthBanner';
import { supabase } from '../lib/supabase';
import { apiRequest } from '../lib/api-request';
import { waitForInitialSession, verifySessionIsLive, getSession, onSessionChange } from '../lib/auth-session';
import { readPendingInvitation } from '../lib/commercial-invitation-token';
import { destinationAfterLogin, recentlyForwardedTo, rememberForwardTo, clearForwardMemory } from '../lib/return-path';
import './Setup.css';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  // Clicking the email-confirmation link lands back in the app with a
  // session already established (detectSessionInUrl -- see lib/supabase.ts)
  // -- often on THIS page, since Supabase's own redirect target isn't
  // guaranteed to skip /login. Without this, nothing ever routes that
  // already-authenticated user anywhere: /login renders unconditionally
  // regardless of session state, so they'd just sit on the login form,
  // and if they then typed their credentials in manually, the "no
  // company yet" guard below would incorrectly treat a brand-new,
  // mid-onboarding account as an existing user with an incomplete setup.
  // Checking once on mount (plus the SIGNED_IN event, for the case where
  // the URL is still being parsed at mount time) and doing a full reload
  // lets AuthenticatedLayout's own one-time gate route them correctly
  // (onboarding / settings / dashboard) instead of stalling here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Root-cause fix (2026-09-20): reads the shared session cache
      // (lib/auth-session.ts) instead of calling supabase.auth.getSession()
      // directly -- same session data, but this can never hang behind
      // GoTrueClient's internal lock.
      const session = await waitForInitialSession();
      if (cancelled || !session) return;

      // getSession() only reads whatever is cached in localStorage -- it
      // does NOT confirm the session's user still exists/is valid
      // server-side. A stale session left over from a deleted/deactivated
      // account passes this check every time, which used to send it
      // straight to '/' and from there into onboarding, where it would
      // only fail later (and confusingly) on the first API call. Found
      // live (2026-09-11). One extra round trip here confirms the session
      // is actually still good before we act on it; a dead one is signed
      // out locally instead, so /login renders normally.
      const liveness = await verifySessionIsLive();
      if (cancelled) return;
      if (liveness === 'invalid') {
        await supabase.auth.signOut();
        return;
      }
      if (liveness === 'timeout') {
        // Couldn't confirm in time -- stay on the login form rather than
        // guessing either way; the user can just log in normally. A
        // timeout is not evidence the session is bad.
        return;
      }
      // Already signed in: on to where they were going -- inside the app (the session and the user data are already loaded, so no page reload). If they were
      // forwarded to this very place a moment ago and are back here, that page does not accept them right now: show the form instead of forwarding again.
      const destination = destinationAfterLogin();
      if (recentlyForwardedTo(destination)) {
        setError("We couldn't open that page with your current session. Please sign in again to continue.");
        return;
      }
      rememberForwardTo(destination);
      navigate(destination, { replace: true });
    })();

    // Covers the case where the URL is still being parsed at mount time
    // (email-confirmation link) -- the session goes from null to non-null
    // sometime after mount instead of already being there. Only redirect
    // on that null->non-null transition, not on every later cache update
    // (e.g. a TOKEN_REFRESHED while sitting on this page shouldn't re-fire
    // this navigation).
    let hadSession = !!getSession();
    const unsubscribe = onSessionChange(() => {
      const session = getSession();
      if (session && !hadSession) {
        window.location.href = destinationAfterLogin();
      }
      hadSession = !!session;
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendConfirmation = async () => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      if (error) throw error;
      setError('Confirmation email resent. Please check your inbox.');
    } catch (error) {
      console.error('Error resending confirmation:', error);
      setError('Failed to resend confirmation email. Please try again.');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowResendButton(false);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message.includes('Email not confirmed')) {
          setError('Please confirm your email address before logging in.');
          setShowResendButton(true);
        } else {
          throw error;
        }
        return;
      }

      if (!data.user) throw new Error('No user data returned');
      clearForwardMemory(); // an explicit sign-in starts clean

      console.log('🎉 === LOGIN SUCCESSFUL ===');
      console.log('👤 User ID:', data.user.id);
      console.log('📧 User Email:', data.user.email);

      // Login is for users who already finished setting up their company --
      // the company+user form only ever belongs to signup (confirm email ->
      // land back in the app already signed in -> onboarding). A login with
      // no company at all means signup was started but never finished; that
      // is an error state here, not a silent redirect into onboarding --
      // resolved with one lightweight existence check (not a `.single()`
      // that throws on 0 rows) before deciding where to send them.
      const membership = (await apiRequest<unknown[]>('api/me/companies'))[0] ?? null;

      // A free client / vendor account (accepted from a commercial invitation) has no company on purpose: it enters, and an invitation waiting for it is accepted.
      const pendingInvitation = readPendingInvitation();
      if (!membership) {
        const profile = await apiRequest<{ account_type: string | null }>('api/me').catch(() => null);
        const external = profile?.account_type === 'client_account' || profile?.account_type === 'vendor_account';
        if (external || pendingInvitation) {
          window.location.href = pendingInvitation ? `/auth/join?token=${encodeURIComponent(pendingInvitation)}` : destinationAfterLogin();
          return;
        }
        await supabase.auth.signOut();
        setError('Your account setup isn\'t complete yet. Please check your email for the confirmation link to finish creating your company, or sign up again.');
        return;
      }

      // Company address status (Settings-lock) is resolved by
      // AuthenticatedLayout's own gate after this redirect -- no need to
      // duplicate that check here too.
      window.location.href = pendingInvitation ? `/auth/join?token=${encodeURIComponent(pendingInvitation)}` : destinationAfterLogin();
    } catch (error) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Error logging in');
    } finally {
      setLoading(false); // ✅ always reset loading
    }
  };

  return (
    <div className="setup-container">
      <div className="setup-content">
        <AuthBanner />

        <h1 className="h1">Login</h1>

        {error && (
          <div className="error-message">
            {error}
            {showResendButton && (
              <StandardButton
                customVariant="white"
                onClick={handleResendConfirmation}
                className="mt-2"
              >
                Resend Confirmation Email
              </StandardButton>
            )}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">Email</label>
            <Input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">Password</label>
            <Input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="mt-4 flex justify-end">
            <StandardButton customVariant="black" type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </StandardButton>
          </div>
        </form>

        <p className="mt-4 text-sm text-gray-600">
          Don&apos;t have an account?{" "}
          <Link to="/auth/signup" className="text-blue-600 hover:underline">
            Sign up here
          </Link>
        </p>
      </div>
    </div>
  );
}
